import {
  GlobalFilterFieldType,
  isGlobalFilterFieldType,
} from "../../types/cms/contentField.guards";
import {
  normalizeSearchParams,
  type SearchParamsInput,
} from "./normalizeSearchParams";

export type FilterOperator =
  | "equals"
  | "contains"
  | "gt"
  | "gte"
  | "lt"
  | "lte";

export const FilterOperatorsEnum = {
  EQUALS: "equals",
  CONTAINS: "contains",
  GT: "gt",
  GTE: "gte",
  LT: "lt",
  LTE: "lte",
} as const satisfies Record<string, FilterOperator>;

export interface Filter {
  fieldId: string;
  operator: FilterOperator;
  value: string;
}

export interface NodeQueryParams {
  search?: string;
  orderBy?: string;
  orderDir?: "asc" | "desc";
  page?: number;
  filters: Filter[];
  skipGlobalFields: GlobalFilterFieldType[];
}

const PARAM_REGEX = /^([^\[\]]+)\[([^\[\]]+)\](?:\[([^\[\]]+)\])?$/;

export const SKIP_GLOBAL_PARAM = "_skipGlobal";
export const SEARCH_PARAM = "search";
export const ORDER_BY_PARAM = "orderBy";
export const ORDER_DIR_PARAM = "orderDir";
export const PAGE_PARAM = "page";

export function parseNodeQueryParams(
  searchParams: SearchParamsInput,
  nodeId: string,
): NodeQueryParams {
  const result: NodeQueryParams = {
    filters: [],
    skipGlobalFields: [],
  };

  const entries = normalizeSearchParams(searchParams);

  for (const [key, value] of entries) {
    const match = key.match(PARAM_REGEX);
    if (!match) continue;

    const [, paramNodeId, field, operator] = match;
    if (paramNodeId !== nodeId) continue;

    switch (field) {
      case SEARCH_PARAM:
        result.search = value;
        break;

      case ORDER_BY_PARAM:
        result.orderBy = value;
        break;

      case ORDER_DIR_PARAM:
        result.orderDir = value as "asc" | "desc";
        break;

      case PAGE_PARAM:
        result.page = Number(value);
        break;

      case SKIP_GLOBAL_PARAM:
        if (isGlobalFilterFieldType(value)) {
          result.skipGlobalFields.push(value);
        }
        break;

      default:
        result.filters.push({
          fieldId: field,
          operator: (operator as FilterOperator) ?? "equals",
          value,
        });
    }
  }

  return result;
}

export function extractNodeIds(
  searchParams: Record<string, string | undefined>,
): string[] {
  const nodeIds = new Set<string>();

  for (const key of Object.keys(searchParams)) {
    const match = key.match(PARAM_REGEX);
    if (match) {
      nodeIds.add(match[1]);
    }
  }

  return Array.from(nodeIds);
}
